import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  MAX_SUPPLIER_PAYMENT_ALLOCATIONS,
  SUPPLIER_PAYMENT_METHODS,
  supplierPaymentNeedsReference,
  type PayableInvoiceSummary,
  type SupplierPaymentMethod,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { useBranch } from '@/hooks/useBranch';
import { useCurrency } from '@/hooks/useCurrency';
import { apiErrorDetails, apiErrorMessage } from '@/services/apiClient';
import {
  usePayableInvoiceQuery,
  usePayableInvoicesQuery,
  usePayableSuppliersQuery,
  usePayablesSummaryQuery,
  usePostSupplierPaymentMutation,
  type SupplierPaymentAllocationInput,
} from '../api/payables.api';
import { DueStateBadge, SettlementBadge } from '../components/PayableBadges';

/** One invoice as it is being settled. The amount stays the text that was typed until it is parsed. */
interface Entry {
  selected: boolean;
  amountInput: string;
}

/** The references the server named when it refused, pulled out of `allocations[PINV-000001]`. */
function refusedReferences(error: unknown): string[] {
  return apiErrorDetails(error)
    .map((detail) => /^allocations\[(.+)\]$/u.exec(detail.field ?? '')?.[1])
    .filter((reference): reference is string => Boolean(reference));
}

/**
 * Recording money paid to a supplier.
 *
 * A page rather than a dialog: one payment can settle several invoices, each with an amount someone
 * has to decide, and that is real work.
 *
 * The supplier and the branch come first because they decide everything else — one payment settles
 * one supplier at one branch in one currency, so the invoices on offer are only ever the compatible
 * ones. That makes the rule structural rather than a refusal after the fact.
 *
 * Nothing is allocated without being typed. A "pay in full" button fills a figure in, where it can
 * be read and changed; the page never quietly spreads an amount across invoices on someone's
 * behalf. The total shown is a preview of the allocations added up — the request carries the
 * allocations alone, and what the payment is worth is whatever the server makes of them.
 */
export function RecordSupplierPaymentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { branches } = useBranch();
  const { format, parse, toInput } = useCurrency();

  const fromInvoiceId = params.get('invoiceId') ?? '';
  const fromInvoice = usePayableInvoiceQuery(fromInvoiceId || undefined);

  const [supplierId, setSupplierId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [method, setMethod] = useState<SupplierPaymentMethod>('CASH');
  const [externalReference, setExternalReference] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [dateTouched, setDateTouched] = useState(false);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [search, setSearch] = useState('');

  // The tenant's own business date, from the server. The browser's clock is in the wrong timezone often enough that defaulting to it would silently back-date or post-date real payments.
  const summary = usePayablesSummaryQuery({});
  const businessDate = summary.data?.asOf ?? '';

  // Only suppliers who are owed something: a payment settles invoices, so one with none to settle is not a choice this page can act on.
  const suppliers = usePayableSuppliersQuery({});

  const open = usePayableInvoicesQuery(
    { supplierId, branchId, outstandingOnly: true, search, pageSize: 100 },
    { enabled: Boolean(supplierId && branchId) },
  );
  const post = usePostSupplierPaymentMutation();

  const invoices = useMemo(() => open.data?.items ?? [], [open.data]);
  const lockedContext = Boolean(fromInvoiceId && fromInvoice.data);

  // Arriving from one invoice fixes the supplier and the branch: this payment is about that bill.
  useEffect(() => {
    const invoice = fromInvoice.data;
    if (!invoice) return;
    setSupplierId(invoice.supplierId);
    setBranchId(invoice.branchId);
    setEntries((current) =>
      current[invoice.purchaseInvoiceId]
        ? current
        : {
            ...current,
            [invoice.purchaseInvoiceId]: {
              selected: true,
              amountInput: toInput(invoice.outstandingMinor),
            },
          },
    );
  }, [fromInvoice.data, toInput]);

  useEffect(() => {
    if (!dateTouched && businessDate) setPaymentDate(businessDate);
  }, [businessDate, dateTouched]);

  const entryFor = (invoice: PayableInvoiceSummary): Entry =>
    entries[invoice.purchaseInvoiceId] ?? { selected: false, amountInput: '' };

  const setEntry = (invoice: PayableInvoiceSummary, patch: Partial<Entry>) =>
    setEntries((current) => ({
      ...current,
      [invoice.purchaseInvoiceId]: { ...entryFor(invoice), ...patch },
    }));

  const chosen = invoices.filter((invoice) => entryFor(invoice).selected);
  const amountOf = (invoice: PayableInvoiceSummary) => parse(entryFor(invoice).amountInput) ?? 0;

  const previewTotal = chosen.reduce((total, invoice) => total + amountOf(invoice), 0);
  const overAllocated = chosen.filter((invoice) => amountOf(invoice) > invoice.outstandingMinor);
  const emptyAmount = chosen.filter((invoice) => amountOf(invoice) <= 0);
  const tooMany = chosen.length > MAX_SUPPLIER_PAYMENT_ALLOCATIONS;

  // Paying before the supplier billed is an advance, which this system does not model. The date is never quietly rewritten when a later invoice joins: the user is told, and corrects it.
  const latestInvoiceDate =
    chosen
      .map((invoice) => invoice.invoiceDate)
      .sort()
      .at(-1) ?? '';
  const beforeInvoices = Boolean(
    paymentDate && latestInvoiceDate && paymentDate < latestInvoiceDate,
  );
  const inFuture = Boolean(paymentDate && businessDate && paymentDate > businessDate);

  const needsReference = supplierPaymentNeedsReference(method);
  const missingReference = needsReference && externalReference.trim().length === 0;

  const refused = post.isError ? refusedReferences(post.error) : [];

  const canPost =
    Boolean(supplierId) &&
    Boolean(branchId) &&
    Boolean(paymentDate) &&
    chosen.length > 0 &&
    !tooMany &&
    overAllocated.length === 0 &&
    emptyAmount.length === 0 &&
    !beforeInvoices &&
    !inFuture &&
    !missingReference &&
    !post.isPending;

  const allocations = (): SupplierPaymentAllocationInput[] =>
    chosen.map((invoice) => ({
      purchaseInvoiceId: invoice.purchaseInvoiceId,
      amountMinor: amountOf(invoice),
    }));

  const submit = () => {
    if (!canPost) return;
    post.mutate(
      {
        paymentDate,
        method,
        ...(externalReference.trim() ? { externalReference: externalReference.trim() } : {}),
        allocations: allocations(),
      },
      {
        onSuccess: (payment) => void navigate(`/finance/payables/payments/${payment.id}`),
        // What each invoice owes has moved on; the entered amounts stay for review rather than being silently reduced to fit, which would pay a figure nobody chose.
        onError: () => void open.refetch(),
      },
    );
  };

  const methodLabel = needsReference
    ? t(`payables.referenceLabels.${method}`)
    : t('payables.referenceLabels.CASH');

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('payables.record.title')}
        subtitle={t('payables.record.subtitle')}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void navigate('/finance/payables/invoices')}
              disabled={post.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} isLoading={post.isPending} disabled={!canPost}>
              {t('payables.record.post')}
            </Button>
          </div>
        }
      />

      {post.isError ? (
        <>
          <Alert tone="error">
            {apiErrorMessage(post.error) ?? t('payables.record.postError')}
          </Alert>
          <Alert tone="info">{t('payables.record.inputKept')}</Alert>
        </>
      ) : null}
      {tooMany ? (
        <Alert tone="warning">
          {t('payables.record.tooManyInvoices', { max: MAX_SUPPLIER_PAYMENT_ALLOCATIONS })}
        </Alert>
      ) : null}
      {beforeInvoices ? (
        <Alert tone="warning">
          {t('payables.record.dateBeforeInvoice', { date: latestInvoiceDate })}
        </Alert>
      ) : null}
      {inFuture ? (
        <Alert tone="warning">{t('payables.record.dateInFuture', { date: businessDate })}</Alert>
      ) : null}

      <Card>
        <CardHeader
          title={t('payables.record.whoSection')}
          subtitle={t('payables.record.whoHint')}
        />
        <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FormField label={t('payables.fields.supplier')} required>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                disabled={lockedContext}
                options={[
                  { value: '', label: t('payables.record.chooseSupplier') },
                  ...(suppliers.data ?? []).map((supplier) => ({
                    value: supplier.supplierId,
                    label: supplier.supplierName,
                  })),
                ]}
                value={supplierId}
                onChange={(event) => {
                  setSupplierId(event.target.value);
                  setEntries({});
                }}
              />
            )}
          </FormField>

          <FormField
            label={t('payables.fields.branch')}
            required
            hint={t('payables.record.branchHint')}
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                disabled={lockedContext}
                options={[
                  { value: '', label: t('payables.record.chooseBranch') },
                  ...branches.map((branch) => ({ value: branch.id, label: branch.name })),
                ]}
                value={branchId}
                onChange={(event) => {
                  setBranchId(event.target.value);
                  setEntries({});
                }}
              />
            )}
          </FormField>

          <Input
            type="date"
            label={t('payables.fields.paymentDate')}
            required
            dir="ltr"
            value={paymentDate}
            max={businessDate || undefined}
            hint={t('payables.record.dateHint')}
            onChange={(event) => {
              setDateTouched(true);
              setPaymentDate(event.target.value);
            }}
          />

          <FormField label={t('payables.fields.method')} required>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                options={SUPPLIER_PAYMENT_METHODS.map((value) => ({
                  value,
                  label: t(`payables.methods.${value}`),
                }))}
                value={method}
                onChange={(event) => setMethod(event.target.value as SupplierPaymentMethod)}
              />
            )}
          </FormField>

          <Input
            label={methodLabel}
            required={needsReference}
            dir="ltr"
            value={externalReference}
            hint={
              needsReference
                ? t('payables.record.referenceRequired')
                : t('payables.record.referenceOptional')
            }
            error={
              missingReference && post.isError ? t('payables.record.referenceMissing') : undefined
            }
            onChange={(event) => setExternalReference(event.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('payables.record.invoicesSection')}
          subtitle={t('payables.record.invoicesHint')}
          action={
            chosen.length > 0 ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  setEntries((current) => {
                    const next = { ...current };
                    for (const invoice of chosen) {
                      next[invoice.purchaseInvoiceId] = {
                        selected: true,
                        amountInput: toInput(invoice.outstandingMinor),
                      };
                    }
                    return next;
                  })
                }
              >
                {t('payables.record.paySelectedInFull')}
              </Button>
            ) : null
          }
          wrapAction
        />
        <CardBody className="space-y-4">
          {!supplierId || !branchId ? (
            <p className="text-muted text-sm">{t('payables.record.chooseContextFirst')}</p>
          ) : (
            <>
              <Input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t('payables.record.searchPlaceholder')}
                aria-label={t('payables.record.searchLabel')}
              />

              {open.isPending ? (
                <p className="text-muted text-sm">{t('common.loading')}</p>
              ) : open.isError ? (
                <Alert tone="error">
                  {apiErrorMessage(open.error) ?? t('payables.invoices.loadError')}
                </Alert>
              ) : invoices.length === 0 ? (
                <p className="text-muted text-sm">{t('payables.record.nothingOutstanding')}</p>
              ) : (
                <ul className="space-y-3">
                  {invoices.map((invoice) => {
                    const entry = entryFor(invoice);
                    const amount = amountOf(invoice);
                    const over = amount > invoice.outstandingMinor;
                    const wasRefused = refused.includes(invoice.reference);

                    return (
                      <li
                        key={invoice.purchaseInvoiceId}
                        className={
                          wasRefused
                            ? 'grid gap-3 rounded-lg border border-red-300 bg-red-50 p-3 lg:grid-cols-[auto_1fr_10rem_11rem] lg:items-end'
                            : 'bg-canvas grid gap-3 rounded-lg p-3 lg:grid-cols-[auto_1fr_10rem_11rem] lg:items-end'
                        }
                      >
                        <input
                          type="checkbox"
                          className="size-4 self-center lg:mb-2.5"
                          checked={entry.selected}
                          aria-label={t('payables.record.selectInvoice', {
                            reference: invoice.reference,
                          })}
                          onChange={(event) =>
                            setEntry(invoice, {
                              selected: event.target.checked,
                              amountInput: event.target.checked
                                ? entry.amountInput || toInput(invoice.outstandingMinor)
                                : entry.amountInput,
                            })
                          }
                        />

                        <div className="min-w-0 space-y-1">
                          <p className="text-ink font-mono text-sm" dir="ltr">
                            {invoice.reference}
                          </p>
                          <p className="text-muted font-mono text-xs" dir="ltr">
                            {invoice.supplierInvoiceNumber}
                          </p>
                          <p className="text-muted text-xs" dir="ltr">
                            {t('payables.record.datesLine', {
                              invoiceDate: invoice.invoiceDate,
                              dueDate: invoice.dueDate,
                            })}
                          </p>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <SettlementBadge settlement={invoice.settlement} />
                            <DueStateBadge invoice={invoice} />
                          </span>
                          {wasRefused ? (
                            <p className="text-xs font-medium text-red-700">
                              {t('payables.record.refreshedRow')}
                            </p>
                          ) : null}
                        </div>

                        <div className="space-y-0.5 text-xs">
                          <p className="text-muted">
                            {t('payables.fields.invoiceTotal')}{' '}
                            <span dir="ltr">{format(invoice.obligationMinor)}</span>
                          </p>
                          <p className="text-muted">
                            {t('payables.fields.allocated')}{' '}
                            <span dir="ltr">{format(invoice.activeAllocatedMinor)}</span>
                          </p>
                          <p className="text-ink font-medium">
                            {t('payables.fields.outstanding')}{' '}
                            <span dir="ltr">{format(invoice.outstandingMinor)}</span>
                          </p>
                        </div>

                        <div className="space-y-1">
                          <Input
                            label={t('payables.fields.allocationAmount')}
                            dir="ltr"
                            inputMode="decimal"
                            value={entry.amountInput}
                            error={over ? t('payables.record.overOutstanding') : undefined}
                            aria-label={t('payables.record.amountFor', {
                              reference: invoice.reference,
                            })}
                            onChange={(event) =>
                              setEntry(invoice, { amountInput: event.target.value })
                            }
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('payables.record.payFullFor', {
                              reference: invoice.reference,
                            })}
                            onClick={() =>
                              setEntry(invoice, {
                                selected: true,
                                amountInput: toInput(invoice.outstandingMinor),
                              })
                            }
                          >
                            {t('payables.record.payFull')}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('payables.record.reviewSection')} />
        <CardBody className="space-y-2 text-sm">
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.supplier')}</span>
            <span className="text-ink" dir="auto">
              {(suppliers.data ?? []).find((supplier) => supplier.supplierId === supplierId)
                ?.supplierName ?? '—'}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.branch')}</span>
            <span className="text-ink" dir="auto">
              {branches.find((branch) => branch.id === branchId)?.name ?? '—'}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.paymentDate')}</span>
            <span className="text-ink" dir="ltr">
              {paymentDate || '—'}
            </span>
          </div>
          <div className="flex flex-wrap justify-between gap-3">
            <span className="text-muted">{t('payables.fields.method')}</span>
            <span className="text-ink">{t(`payables.methods.${method}`)}</span>
          </div>

          <ul className="border-line space-y-1 border-t pt-2">
            {chosen.map((invoice) => (
              <li key={invoice.purchaseInvoiceId} className="flex flex-wrap justify-between gap-3">
                <span className="text-muted font-mono text-xs" dir="ltr">
                  {invoice.reference}
                </span>
                <span className="text-ink text-xs" dir="ltr">
                  {t('payables.record.reviewRow', {
                    before: format(invoice.outstandingMinor),
                    now: format(amountOf(invoice)),
                    after: format(Math.max(0, invoice.outstandingMinor - amountOf(invoice))),
                  })}
                </span>
              </li>
            ))}
          </ul>

          <div className="border-line flex flex-wrap justify-between gap-3 border-t pt-2">
            <span className="text-ink font-medium">{t('payables.record.previewTotal')}</span>
            <span className="text-ink font-medium" dir="ltr">
              {format(previewTotal)}
            </span>
          </div>
          <p className="text-muted text-xs">{t('payables.record.previewHint')}</p>
          <p className="text-muted text-xs">{t('payables.record.postingExplains')}</p>
        </CardBody>
      </Card>
    </div>
  );
}
